import { create } from 'zustand';
import { User, UserRole, RegisterRequest } from '@/types';
import { getData, setData } from '@/services/local/storage';

interface AuthState {
  user: User | null;
  users: User[];
  registerRequests: RegisterRequest[];
  isLoading: boolean;
  error: string | null;
  login: (phoneNumber: string, otp: string) => Promise<boolean>;
  logout: () => void;
  updateUsername: (userId: string, username: string) => void;
  updateUserPhone: (userId: string, phoneNumber: string) => void;
  updateUserPassword: (userId: string, password: string) => void;
  addUser: (userData: { 
    username: string; 
    phoneNumber: string; 
    role: UserRole;
  }) => void;
  addRegisterRequest: (request: RegisterRequest) => void;
  approveRegisterRequest: (requestId: string, role: UserRole) => void;
  rejectRegisterRequest: (requestId: string) => void;
  deleteUser: (userId: string) => void;
}

const initialState = {
  user: getData('currentUser', null),
  users: getData('users', []),
  registerRequests: getData('registerRequests', []),
  isLoading: false,
  error: null,
};

export const useAuthStore = create<AuthState>((set, get) => ({
  ...initialState,

  login: async (phoneNumber, otp) => {
    set({ isLoading: true, error: null });
    try {
      const users = get().users;
      const user = users.find(u => u.phoneNumber === phoneNumber);
      
      if (!user) {
        throw new Error('رقم الهاتف غير مسجل');
      }

      // For development, always accept '123456' as OTP
      if (otp !== '123456') {
        throw new Error('رمز التحقق غير صحيح');
      }

      set({ user });
      setData('currentUser', user);
      return true;
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'فشل تسجيل الدخول' });
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  logout: () => {
    set({ user: null });
    setData('currentUser', null);
  },

  updateUsername: (userId, username) => {
    set(state => {
      const newUsers = state.users.map(user =>
        user.id === userId ? { ...user, username } : user
      );
      
      // Update current user if it's them
      const newUser = state.user?.id === userId ? 
        { ...state.user, username } : 
        state.user;

      setData('users', newUsers);
      setData('currentUser', newUser);
      
      return { 
        users: newUsers,
        user: newUser
      };
    });
  },

  updateUserPhone: (userId, phoneNumber) => {
    set(state => {
      const newUsers = state.users.map(user =>
        user.id === userId ? { ...user, phoneNumber } : user
      );
      
      // Update current user if it's them
      const newUser = state.user?.id === userId ? 
        { ...state.user, phoneNumber } : 
        state.user;

      setData('users', newUsers);
      setData('currentUser', newUser);
      
      return { 
        users: newUsers,
        user: newUser
      };
    });
  },

  updateUserPassword: (userId, password) => {
    set(state => {
      const newUsers = state.users.map(user =>
        user.id === userId ? { ...user, password } : user
      );
      setData('users', newUsers);
      return { users: newUsers };
    });
  },

  addUser: (userData) => {
    const newUser = {
      id: crypto.randomUUID().replace(/[-]/g, ""),
      ...userData,
      permissions: userData.role === 'admin' ? ['*'] : ['view', 'add']
    };
    
    set(state => {
      const newUsers = [...state.users, newUser];
      setData('users', newUsers);
      return { users: newUsers };
    });
  },

  addRegisterRequest: (request) => {
    set(state => {
      const newRequests = [...state.registerRequests, request];
      setData('app_data', { ...get(), registerRequests: newRequests });
      return { registerRequests: newRequests };
    });
  },

  approveRegisterRequest: (requestId, role) => {
    const state = get();
    const request = state.registerRequests.find(r => r.id === requestId);
    if (!request) return;

    // Check if user with this phone number already exists
    const existingUser = state.users.find(u => u.phoneNumber === request.data.phoneNumber);
    if (existingUser) {
      console.warn('User with this phone number already exists');
      return;
    }

    // Create new user
    const newUser = {
      id: crypto.randomUUID().replace(/[-]/g, ""),
      username: request.data.name,
      phoneNumber: request.data.phoneNumber,
      role,
      permissions: role === 'admin' ? ['*'] : ['view', 'add']
    };

    // Update request status
    const updatedRequests = state.registerRequests.map(r =>
      r.id === requestId ? { ...r, status: 'approved' as const } : r
    );

    // Update state
    set({
      users: [...state.users, newUser],
      registerRequests: updatedRequests
    });

    // Update storage
    setData('users', [...state.users, newUser]);
    setData('app_data', { ...get(), registerRequests: updatedRequests });
  },

  rejectRegisterRequest: (requestId) => {
    set(state => {
      const updatedRequests = state.registerRequests.map(r =>
        r.id === requestId ? { ...r, status: 'rejected' as const } : r
      );
      setData('app_data', { ...get(), registerRequests: updatedRequests });
      return { registerRequests: updatedRequests };
    });
  },

  deleteUser: (userId) => {
    set(state => {
      const newUsers = state.users.filter(user => user.id !== userId);
      setData('users', newUsers);
      return { users: newUsers };
    });
  }
}));